import React, { useState } from 'react';
import styled from 'styled-components';

const DiagnosticContainer = styled.div`
  max-width: 1000px;
  margin: 2rem auto;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const TestButton = styled.button`
  background-color: #007cba;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  margin: 1rem 0;
  
  &:hover {
    background-color: #005a87;
  }
`;

const ResultContainer = styled.div`
  background-color: #f5f5f5;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 0.9rem;
  max-height: 400px;
  overflow-y: auto;
`;

const ErrorContainer = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  border-left: 4px solid #c62828;
`;

const SuccessContainer = styled.div`
  background-color: #e8f5e8;
  color: #2e7d32;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  border-left: 4px solid #2e7d32;
`;

const WooCommerceDiagnostic = () => {
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const runDiagnostic = async () => {
    setLoading(true);
    setError('');
    setResult('Running WooCommerce Diagnostic...\n\n');
    
    try {
      // Test 1: Check if WooCommerce endpoint exists
      setResult(prev => prev + 'Test 1: Checking WooCommerce REST API endpoint...\n');
      const wcResponse = await fetch('http://localhost:8888/Website/duchessCollection/wp-json/wc/v3/');
      setResult(prev => prev + `Status: ${wcResponse.status} ${wcResponse.statusText}\n`);
      
      if (wcResponse.ok) {
        const wcData = await wcResponse.json();
        setResult(prev => prev + `✅ WooCommerce API endpoint accessible\n`);
        setResult(prev => prev + `Available endpoints: ${Object.keys(wcData).join(', ')}\n\n`);
      } else {
        setResult(prev => prev + `❌ WooCommerce API endpoint not accessible\n\n`);
      }

      // Test 2: Check WordPress REST API
      setResult(prev => prev + 'Test 2: Checking WordPress REST API...\n');
      const wpResponse = await fetch('http://localhost:8888/Website/duchessCollection/wp-json/');
      setResult(prev => prev + `Status: ${wpResponse.status} ${wpResponse.statusText}\n`);
      
      if (wpResponse.ok) {
        const wpData = await wpResponse.json();
        setResult(prev => prev + `✅ WordPress REST API accessible\n`);
        setResult(prev => prev + `Available namespaces: ${wpData.namespaces ? wpData.namespaces.join(', ') : 'Unknown'}\n\n`);
      } else {
        setResult(prev => prev + `❌ WordPress REST API not accessible\n\n`);
      }

      // Test 3: Test authentication with different methods
      setResult(prev => prev + 'Test 3: Testing authentication methods...\n');
      
      // Method 1: Basic Auth
      const credentials = btoa('ck_8c490c6c92d07347d264eb393813e76c141f64ae:cs_f5e70b5387cf4e6e7f2549ea67fac7e949991879');
      const authResponse = await fetch('http://localhost:8888/Website/duchessCollection/wp-json/wc/v3/products?per_page=1', {
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/json'
        }
      });
      
      setResult(prev => prev + `Basic Auth Status: ${authResponse.status} ${authResponse.statusText}\n`);
      
      if (authResponse.ok) {
        const products = await authResponse.json();
        setResult(prev => prev + `✅ Authentication successful! Found ${products.length} products\n\n`);
      } else {
        const errorData = await authResponse.json().catch(() => ({}));
        setResult(prev => prev + `❌ Authentication failed: ${errorData.message || 'Unknown error'}\n\n`);
      }

      // Test 4: Check if we can access system status
      setResult(prev => prev + 'Test 4: Checking WooCommerce system status...\n');
      const systemResponse = await fetch('http://localhost:8888/Website/duchessCollection/wp-json/wc/v3/system_status', {
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/json'
        }
      });
      
      setResult(prev => prev + `System Status: ${systemResponse.status} ${systemResponse.statusText}\n`);
      
      if (systemResponse.ok) {
        const systemData = await systemResponse.json();
        setResult(prev => prev + `✅ System status accessible\n`);
        setResult(prev => prev + `WooCommerce Version: ${systemData.environment?.version || 'Unknown'}\n`);
        setResult(prev => prev + `WordPress Version: ${systemData.environment?.wp_version || 'Unknown'}\n\n`);
      } else {
        setResult(prev => prev + `❌ System status not accessible\n\n`);
      }

      // Test 5: Check orders endpoint (different permission level)
      setResult(prev => prev + 'Test 5: Testing orders endpoint...\n');
      const ordersResponse = await fetch('http://localhost:8888/Website/duchessCollection/wp-json/wc/v3/orders?per_page=1', {
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/json'
        }
      });
      
      setResult(prev => prev + `Orders Status: ${ordersResponse.status} ${ordersResponse.statusText}\n`);
      
      if (ordersResponse.ok) {
        setResult(prev => prev + `✅ Orders endpoint accessible\n\n`);
      } else {
        const ordersError = await ordersResponse.json().catch(() => ({}));
        setResult(prev => prev + `❌ Orders endpoint failed: ${ordersError.message || 'Unknown error'}\n\n`);
      }

      setResult(prev => prev + '🎯 Diagnostic Complete!\n\n');
      setResult(prev => prev + 'Next Steps:\n');
      setResult(prev => prev + '1. If authentication failed, check API key permissions in WordPress admin\n');
      setResult(prev => prev + '2. Go to WooCommerce > Settings > Advanced > REST API\n');
      setResult(prev => prev + '3. Verify the API key has "Read/Write" permissions\n');
      setResult(prev => prev + '4. Check that the user associated with the API key is an Administrator\n');
      setResult(prev => prev + '5. Try regenerating the API key if issues persist\n');
      
    } catch (err) {
      setError(`❌ Diagnostic Failed: ${err.message}`);
      setResult(prev => prev + `❌ Error: ${err.message}\n\n`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DiagnosticContainer>
      <h2>WooCommerce Diagnostic Tool</h2>
      <p>This tool will help diagnose WooCommerce API connection issues by testing various endpoints and authentication methods.</p>
      
      <TestButton onClick={runDiagnostic} disabled={loading}>
        {loading ? 'Running Diagnostic...' : 'Run WooCommerce Diagnostic'}
      </TestButton>
      
      {error && <ErrorContainer>{error}</ErrorContainer>}
      
      {result && (
        <ResultContainer>
          {result}
        </ResultContainer>
      )}
    </DiagnosticContainer>
  );
};

export default WooCommerceDiagnostic;